import { z } from 'zod';

// src/storage/idb.ts
function indexedDbAvailable() {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}
var DEFAULT_DB_NAME = "authe";
var DEFAULT_STORE_NAME = "kv";
function createMemoryKvStore() {
  const map = /* @__PURE__ */ new Map();
  return {
    async get(key) {
      return map.has(key) ? map.get(key) : void 0;
    },
    async set(key, value) {
      map.set(key, value);
    },
    async delete(key) {
      map.delete(key);
    },
    async keys(prefix) {
      const all = [...map.keys()];
      return prefix ? all.filter((k) => k.startsWith(prefix)) : all;
    },
    async clearPrefix(prefix) {
      let removed = 0;
      for (const key of [...map.keys()]) {
        if (key.startsWith(prefix)) {
          map.delete(key);
          removed += 1;
        }
      }
      return removed;
    }
  };
}
function createIdbKvStore(opts = {}) {
  const dbName = opts.dbName ?? DEFAULT_DB_NAME;
  const storeName = opts.storeName ?? DEFAULT_STORE_NAME;
  if (!indexedDbAvailable()) return createMemoryKvStore();
  let dbPromise = null;
  let fallback = null;
  function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(dbName, 1);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName);
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    return dbPromise;
  }
  function memoryFallback() {
    if (!fallback) fallback = createMemoryKvStore();
    return fallback;
  }
  function tx(mode, run) {
    return openDb().then((db) => {
      if (!db) return void 0;
      return new Promise((resolve) => {
        try {
          const transaction = db.transaction(storeName, mode);
          const request = run(transaction.objectStore(storeName));
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => resolve(void 0);
          transaction.onerror = () => resolve(void 0);
          transaction.onabort = () => resolve(void 0);
        } catch {
          resolve(void 0);
        }
      });
    });
  }
  return {
    async get(key) {
      const db = await openDb();
      if (!db) return memoryFallback().get(key);
      const raw = await tx("readonly", (s2) => s2.get(key));
      return raw ? raw.v : void 0;
    },
    async set(key, value) {
      const db = await openDb();
      if (!db) return memoryFallback().set(key, value);
      const envelope = { v: value, t: Date.now() };
      await tx("readwrite", (s2) => s2.put(envelope, key));
    },
    async delete(key) {
      const db = await openDb();
      if (!db) return void memoryFallback().delete(key);
      await tx("readwrite", (s2) => s2.delete(key));
    },
    async keys(prefix) {
      const db = await openDb();
      if (!db) return memoryFallback().keys(prefix);
      const range = prefix != null ? IDBKeyRange.bound(prefix, `${prefix}\uFFFF`, false, false) : void 0;
      const result = await tx(
        "readonly",
        (s2) => s2.getAllKeys(range)
      );
      return (result ?? []).map(String);
    },
    async clearPrefix(prefix) {
      const db = await openDb();
      if (!db) return memoryFallback().clearPrefix(prefix);
      const matching = await this.keys(prefix);
      await Promise.all(matching.map((key) => this.delete(key)));
      return matching.length;
    }
  };
}
var ScopedTxCache = class _ScopedTxCache {
  constructor(store, identity) {
    this.store = store;
    this.scope = new ScopedStorage(identity);
  }
  store;
  scope;
  /** Cache key for one transaction body, content-addressed by its etag. */
  bodyKey(canonicalTxId, etag) {
    return this.scope.keyAt(
      ["body", canonicalTxId, etag ?? "noetag"].join(":"),
      this.scope.defaultLevel
    );
  }
  /** Cache key for a static/system blob, addressed by its deterministic S3 key. */
  staticKey(s3Key) {
    return this.scope.keyAt(["static", s3Key].join(":"), this.scope.defaultLevel);
  }
  /**
   * Cache key for one offloaded body slice, content-addressed by the SLICE's
   * own etag (each sibling S3 object has its own), so re-decoding one slice
   * never invalidates the cached core or a sibling slice.
   */
  sliceKey(canonicalTxId, slice, etag) {
    return this.scope.keyAt(
      ["slice", canonicalTxId, slice, etag ?? "noetag"].join(":"),
      this.scope.defaultLevel
    );
  }
  getBody(canonicalTxId, etag) {
    return this.store.get(this.bodyKey(canonicalTxId, etag));
  }
  setBody(canonicalTxId, etag, value) {
    return this.store.set(this.bodyKey(canonicalTxId, etag), value);
  }
  getSlice(canonicalTxId, slice, etag) {
    return this.store.get(this.sliceKey(canonicalTxId, slice, etag));
  }
  setSlice(canonicalTxId, slice, etag, value) {
    return this.store.set(this.sliceKey(canonicalTxId, slice, etag), value);
  }
  getStatic(s3Key) {
    return this.store.get(this.staticKey(s3Key));
  }
  setStatic(s3Key, value) {
    return this.store.set(this.staticKey(s3Key), value);
  }
  /**
   * Drop every cached entry at `level` and narrower for this identity — call on
   * logout (`"user"`) or when leaving a tenant (`"tenant"`), mirroring
   * `ScopedStorage.clear`.
   */
  clear(level) {
    const prefix = `${this.scope.keyAt("", level).replace(/:$/, "")}:`;
    return this.store.clearPrefix(prefix);
  }
  /** Derive a cache bound to a different identity (e.g. after a tenant switch). */
  with(patch) {
    return new _ScopedTxCache(this.store, { ...this.scope.identity, ...patch });
  }
};
function createScopedTxCache(identity, store) {
  return new ScopedTxCache(store ?? createIdbKvStore(), identity);
}

// src/storage/cdn-cookies.ts
var config = {};
var session = null;
var inflight = null;
var negativeUntil = 0;
var DEFAULT_NEGATIVE_TTL_SEC = 30;
function configureCdnDelivery(next) {
  config = { ...config, ...next };
  session = null;
  inflight = null;
  negativeUntil = 0;
}
function clearCdnSession() {
  session = null;
  inflight = null;
  negativeUntil = 0;
}
function invalidateCdnSession() {
  session = null;
  inflight = null;
  negativeUntil = 0;
  config.persist?.clear();
}
function getCdnSession() {
  return session;
}
function cdnBaseUrl() {
  return config.cdnBaseUrl ?? session?.cdnBaseUrl;
}
function nowSec() {
  return Math.floor(Date.now() / 1e3);
}
function valid(s2, marginSec) {
  if (!s2 || !s2.signedCookieDelivery) return false;
  if (!s2.expiresAt) return true;
  return s2.expiresAt - marginSec > nowSec();
}
async function ensureCdnSession() {
  const margin = config.refreshMarginSec ?? 60;
  if (valid(session, margin)) return session;
  if (session && !session.signedCookieDelivery && nowSec() < negativeUntil) return session;
  if (!session) {
    const persisted = config.persist?.load() ?? null;
    if (valid(persisted, margin)) {
      session = { ...persisted, cdnBaseUrl: persisted.cdnBaseUrl ?? config.cdnBaseUrl };
      return session;
    }
  }
  if (!config.mint) {
    session = { signedCookieDelivery: false, reason: "no_mint_configured", cdnBaseUrl: config.cdnBaseUrl };
    markNegative();
    return session;
  }
  if (!inflight) {
    const mint = config.mint;
    inflight = (async () => {
      try {
        const r = await mint();
        session = { ...r, cdnBaseUrl: r.cdnBaseUrl ?? config.cdnBaseUrl };
        if (session.signedCookieDelivery) config.persist?.save(session);
        else markNegative();
      } catch (err) {
        session = {
          signedCookieDelivery: false,
          reason: err instanceof Error ? err.message : "mint_failed",
          cdnBaseUrl: config.cdnBaseUrl
        };
        markNegative();
      } finally {
        inflight = null;
      }
      return session;
    })();
  }
  return inflight;
}
function markNegative() {
  const ttl = config.negativeTtlSec ?? DEFAULT_NEGATIVE_TTL_SEC;
  negativeUntil = ttl > 0 ? nowSec() + ttl : 0;
}
function signedCdnUrl(key, base) {
  return `${base.replace(/\/+$/, "")}/${String(key).replace(/^\//, "")}`;
}
var PUBLIC_KEY_SEGMENT = /(^|\/)public\//;
function isPublicCdnKey(key) {
  return PUBLIC_KEY_SEGMENT.test(String(key).replace(/^\/+/, ""));
}
var CdnAccessDeniedError = class extends Error {
  /** The offending CDN object key. */
  key;
  /** Stable code for UI branching / telemetry. */
  code = "cdn_cross_scope_denied";
  constructor(key, message) {
    super(message ?? `CDN object is outside your access scope: ${key}`);
    this.name = "CdnAccessDeniedError";
    this.key = key;
  }
};
function normalizeCdnKey(key) {
  return String(key).replace(/^\/+/, "");
}
function prefixCovers(key, prefix) {
  const p = normalizeCdnKey(prefix).replace(/\/+$/, "");
  if (!p) return false;
  return key === p || key.startsWith(`${p}/`);
}
var PRIVATE_BODY_ROOT = "pipeline/bodies/private/";
var PRIVATE_ASSET_ROOT = "assets/private/";
function isPrivateCdnKey(key) {
  return !isPublicCdnKey(key);
}
function isPrivateAssetCdnKey(key) {
  return normalizeCdnKey(key).startsWith(PRIVATE_ASSET_ROOT);
}
function cdnKeyDenialReason(key, session2) {
  if (isPublicCdnKey(key)) return null;
  const k = normalizeCdnKey(key);
  if (k.startsWith(PRIVATE_ASSET_ROOT)) {
    const readable2 = session2?.privateAssetPrefixes ?? [];
    if (readable2.length === 0) return null;
    return readable2.some((p) => prefixCovers(k, p)) ? null : "cross_scope_denied";
  }
  if (!k.startsWith(PRIVATE_BODY_ROOT)) return null;
  const readable = [...session2?.privatePrefixes ?? [], ...session2?.sharedPrefixes ?? []];
  if (readable.length === 0) return null;
  if (readable.some((p) => prefixCovers(k, p))) return null;
  return "cross_scope_denied";
}
function isCdnKeyReadable(key, session2) {
  return cdnKeyDenialReason(key, session2) === null;
}
async function fetchSignedCdnJson(key, init) {
  const isPublic = isPublicCdnKey(key);
  let base;
  if (isPublic) {
    base = config.cdnBaseUrl ?? getCdnSession()?.cdnBaseUrl;
  } else {
    const s2 = await ensureCdnSession();
    base = s2.cdnBaseUrl ?? config.cdnBaseUrl;
    if (!s2.signedCookieDelivery || !base) {
      throw new Error(`signed CDN delivery unavailable${s2.reason ? ` (${s2.reason})` : ""}`);
    }
    if (cdnKeyDenialReason(key, s2)) throw new CdnAccessDeniedError(key);
  }
  if (!base) throw new Error("CDN delivery unavailable (no_cdn_base_url)");
  const res = await fetch(signedCdnUrl(key, base), {
    ...init,
    // Public: send NO cookies (wildcard ACAO forbids credentials).
    // Private: send the CloudFront signed cookies.
    credentials: isPublic ? "omit" : "include",
    headers: { Accept: "application/json", ...init?.headers ?? {} }
  });
  if (!res.ok) {
    if (!isPublic && (res.status === 401 || res.status === 403)) invalidateCdnSession();
    throw new Error(`${isPublic ? "public" : "signed"} CDN fetch failed: ${res.status} ${res.statusText}`);
  }
  return await res.json();
}
async function prepareSignedCdnImage(key) {
  if (isPublicCdnKey(key)) {
    const base2 = config.cdnBaseUrl ?? getCdnSession()?.cdnBaseUrl;
    if (!base2) return { denied: false, reason: "no_cdn_base_url" };
    return { src: signedCdnUrl(key, base2), crossOrigin: "anonymous", denied: false };
  }
  const s2 = await ensureCdnSession();
  const base = s2.cdnBaseUrl ?? config.cdnBaseUrl;
  if (!s2.signedCookieDelivery || !base) {
    return { denied: false, reason: `signed_delivery_unavailable${s2.reason ? `:${s2.reason}` : ""}` };
  }
  if (cdnKeyDenialReason(key, s2)) return { denied: true, reason: "cross_scope_denied" };
  return { src: signedCdnUrl(key, base), crossOrigin: "use-credentials", denied: false };
}

// src/storage/loader.ts
async function defaultFetchJson(url, init) {
  const res = await fetch(url, init);
  if (!res.ok) {
    throw new Error(`fetch ${url} failed: ${res.status} ${res.statusText}`);
  }
  return res.json();
}
function versionedKey(key, etag) {
  return etag ? `${key}?v=${encodeURIComponent(etag)}` : key;
}
function createEnvelopeBodyLoader(config2) {
  const fetchJson = config2.fetchJson ?? defaultFetchJson;
  function resolveBodyUrl(s3Key, canonicalTxId, slice) {
    if (config2.resolveBodyUrl) return config2.resolveBodyUrl(s3Key, canonicalTxId, slice);
    if (config2.cdn) return cdnUrlForKey(s3Key, config2.cdn);
    throw new Error(
      "createEnvelopeBodyLoader: no resolveBodyUrl and no cdn config \u2014 cannot resolve an S3 key to a URL."
    );
  }
  async function fetchOne(key, canonicalTxId, slice) {
    if (config2.fetchKey) return config2.fetchKey(key);
    return fetchJson(await resolveBodyUrl(key, canonicalTxId, slice));
  }
  async function fetchFirstReadable(keys, opts = {}) {
    for (const key of keys) {
      try {
        const payload = await fetchOne(
          versionedKey(key, opts.etag),
          opts.canonicalTxId,
          opts.slice
        );
        return { key, payload };
      } catch (err) {
        if (err instanceof CdnAccessDeniedError) continue;
        throw err;
      }
    }
    return null;
  }
  function loadBody(header, inlineBody) {
    const { canonical_tx_id, s3_etag } = header;
    const fetchBody = async (s3Key) => {
      const cached = await config2.cache.getBody(canonical_tx_id, s3_etag);
      if (cached) return cached;
      const url = await resolveBodyUrl(s3Key, canonical_tx_id);
      const body = await fetchJson(url);
      await config2.cache.setBody(canonical_tx_id, s3_etag, body);
      return body;
    };
    if (inlineBody) {
      void config2.cache.setBody(canonical_tx_id, s3_etag, inlineBody);
      return loadEnvelope(header, fetchBody, inlineBody).body;
    }
    return loadEnvelope(header, fetchBody).body;
  }
  async function loadSlice(header, slice) {
    const ref = header.body_slices?.[slice];
    if (!ref || !header.s3_key) return void 0;
    const key = sliceKey(header.s3_key, slice);
    if (!key) return void 0;
    const sliceEtag = ref.etag ?? header.s3_etag ?? null;
    const cached = await config2.cache.getSlice(header.canonical_tx_id, slice, sliceEtag);
    if (cached !== void 0) return cached;
    const url = await resolveBodyUrl(key, header.canonical_tx_id, slice);
    const payload = await fetchJson(url);
    await config2.cache.setSlice(header.canonical_tx_id, slice, sliceEtag, payload);
    return payload;
  }
  async function loadFullBody(header, opts = {}) {
    const core = await loadBody(header, opts.inlineBody).get();
    const manifest = header.body_slices ?? {};
    const wanted = (opts.include ?? Object.keys(manifest)).filter(
      (slice) => slice !== SLICE_CORE && manifest[slice]
    );
    if (wanted.length === 0) return core;
    const merged = cloneBody(core);
    await Promise.all(
      wanted.map(async (slice) => {
        const payload = await loadSlice(header, slice);
        if (payload !== void 0) {
          mergeSlice(merged, slice, payload);
        }
      })
    );
    return merged;
  }
  async function loadCore(ref) {
    const { canonicalTxId, keys, etag } = ref;
    if (canonicalTxId) {
      const cached = await config2.cache.getBody(canonicalTxId, etag);
      if (cached !== void 0) return { key: keys[0] ?? "", body: cached };
    }
    const hit = await fetchFirstReadable(keys, { canonicalTxId, etag });
    if (!hit) return null;
    if (canonicalTxId) await config2.cache.setBody(canonicalTxId, etag, hit.payload);
    return { key: hit.key, body: hit.payload };
  }
  async function loadStatic(staticS3Key) {
    const cached = await config2.cache.getStatic(staticS3Key);
    if (cached !== void 0) return cached;
    const url = config2.cdn ? cdnUrlForKey(staticS3Key, config2.cdn) : await resolveBodyUrl(staticS3Key);
    const data = await fetchJson(url);
    await config2.cache.setStatic(staticS3Key, data);
    return data;
  }
  return { loadBody, loadCore, loadSlice, loadFullBody, loadStatic, fetchFirstReadable };
}
function cloneBody(body) {
  const sc = globalThis.structuredClone;
  if (typeof sc === "function") return sc(body);
  return JSON.parse(JSON.stringify(body));
}

// src/storage/bulk.ts
var DEFAULTS = { concurrency: 4, maxAttempts: 2, flushMs: 120 };
function defaultScheduler(run, ms) {
  const id = setTimeout(run, ms);
  return () => clearTimeout(id);
}
var BulkLoader = class {
  results = /* @__PURE__ */ new Map();
  claimed = /* @__PURE__ */ new Set();
  attempts = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  queue = [];
  cancelFlush = null;
  running = 0;
  stopped = false;
  concurrency;
  maxAttempts;
  flushMs;
  schedule;
  constructor(options = {}) {
    this.concurrency = Math.max(1, options.concurrency ?? DEFAULTS.concurrency);
    this.maxAttempts = Math.max(1, options.maxAttempts ?? DEFAULTS.maxAttempts);
    this.flushMs = options.flushMs ?? DEFAULTS.flushMs;
    this.schedule = options.scheduleFlush ?? defaultScheduler;
  }
  /** Notified once per batch of resolutions, not once per resolution. */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => void this.listeners.delete(listener);
  }
  /** A settled result, or `undefined` when the key hasn't been asked yet. */
  get(key) {
    return this.results.get(key);
  }
  /** Snapshot of everything settled so far. */
  snapshot() {
    return new Map(this.results);
  }
  /** True while at least one load is in flight. */
  get isLoading() {
    return this.running > 0;
  }
  /**
   * Replace the queue and start working it.
   *
   * Replacing rather than appending because the caller's list is the truth: rows
   * scroll out of the feed, and work queued for a row nobody can see any more is
   * work taken from one they can. Already-settled and in-flight keys are left
   * alone, so a list that grew by one row does not restart the other hundred.
   */
  enqueue(tasks) {
    if (this.stopped) return;
    this.queue = tasks.filter((task) => !this.results.has(task.key));
    this.pump();
  }
  /**
   * Stop taking new work.
   *
   * In-flight loads are deliberately NOT aborted: the list changing (a new
   * transaction, a finality tick) shouldn't throw away a body already on the
   * wire, and it will be wanted by whatever replaces this loader.
   */
  stop() {
    this.stopped = true;
    this.cancelFlush?.();
    this.cancelFlush = null;
    this.listeners.clear();
  }
  take() {
    for (const task of this.queue) {
      if (this.results.has(task.key) || this.claimed.has(task.key)) continue;
      if ((this.attempts.get(task.key) ?? 0) >= this.maxAttempts) {
        this.results.set(task.key, null);
        this.notify();
        continue;
      }
      this.claimed.add(task.key);
      return task;
    }
    return null;
  }
  pump() {
    while (this.running < this.concurrency) {
      const task = this.take();
      if (!task) return;
      void this.run(task);
    }
  }
  async run(task) {
    this.attempts.set(task.key, (this.attempts.get(task.key) ?? 0) + 1);
    this.running += 1;
    if (this.running === 1) this.notify();
    try {
      this.results.set(task.key, await task.load());
      this.notify();
    } catch {
    } finally {
      this.claimed.delete(task.key);
      this.running -= 1;
      if (this.running === 0) this.notify();
      if (!this.stopped) this.pump();
    }
  }
  notify() {
    if (this.cancelFlush || this.stopped) return;
    this.cancelFlush = this.schedule(() => {
      this.cancelFlush = null;
      for (const listener of this.listeners) listener();
    }, this.flushMs);
  }
};
function capDistinct(items, keyOf, max) {
  const taken = [];
  const keys = [];
  const seen = /* @__PURE__ */ new Set();
  let eligible = 0;
  for (const item of items) {
    const key = keyOf(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    eligible += 1;
    if (taken.length >= max) continue;
    taken.push(item);
    keys.push(key);
  }
  return { taken, keys, dropped: eligible - taken.length };
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  OTHER: "other"
};

// src/chains/seed-data.ts
var CHAIN_MAPPING = {
  // --- EVM mainnets ---
  "0x1": "eth",
  "0x3d": "etc",
  "0x89": "polygon",
  "0x38": "bsc",
  "0xcc": "opbnb",
  "0xa86a": "avalanche",
  "0xfa": "fantom",
  "0x19": "cronos",
  "0x2a15c308d": "palm",
  "0xa4b1": "arbitrum",
  "0x64": "gnosis",
  "0x15b38": "chiliz",
  "0x2105": "base",
  "0xa": "optimism",
  "0x2019": "klaytn",
  "0xa4ec": "celo",
  "0x8ae": "kava",
  "0x440": "metis",
  "0x63564c40": "harmony",
  "0x171": "pulsechain",
  "0x1388": "mantle",
  "0x505": "moonriver",
  "0x504": "moonbeam",
  "0x144": "zkSync_era",
  "0x44d": "polygon_zkevm",
  "0xe708": "linea",
  "0x82750": "scroll",
  "0x13e31": "blast",
  "0x4e454152": "aurora",
  "0x7a": "fuse",
  "0x2eb": "flow",
  "0x7e4": "ronin",
  "0x46f": "lisk",
  "0x1b58": "zetachain",
  "0x531": "sei",
  "0x8f": "monad",
  "0x2611": "plasma",
  // --- EVM testnets ---
  "0x5": "goerli",
  "0xaa36a7": "sepolia",
  "0x4268": "holesky",
  "0x13881": "mumbai",
  "0x13882": "polygon_amoy",
  "0x61": "bsc_testnet",
  "0x27d8": "gnosis_chiado",
  "0x15b32": "chiliz_spicy",
  "0x14a33": "base_goerli",
  "0x14a34": "base_sepolia",
  "0x1a4": "optimism_goerli",
  "0xaa37dc": "optimism_sepolia",
  "0x66eee": "arbitrum_sepolia",
  "0x3e9": "klaytn_baobab",
  "0xaef3": "celo_alfajores",
  "0x8ad": "kava_testnet",
  "0x24c": "metis_goerli",
  "0x6357d2e0": "harmony_testnet",
  "0x4e454153": "aurora_testnet",
  "0x507": "moonbase",
  "0x118": "zkSync_testnet",
  "0x145": "zkSync_era_testnet",
  "0x12c": "zksync_sepolia",
  "0x7b": "fuse_testnet",
  "0xe704": "linea_testnet",
  "0xe705": "linea_sepolia",
  "0x8274f": "scroll_testnet",
  "0x138b": "mantle_sepolia",
  "0xa0c71fd": "blast_sepolia",
  "0x98a": "polygon_zkevm_cardona",
  "0xfa2": "fantom_testnet",
  "0x106a": "lisk_sepolia",
  "0x221": "flow_testnet",
  "0x7e5": "ronin_saigon",
  "0x1b59": "zetachain_testnet",
  "0x530": "sei_testnet"
};
var TESTNET_CHAINS = [
  "goerli",
  "sepolia",
  "mumbai",
  "bsc_testnet",
  "gnosis_chiado",
  "chiliz_spicy",
  "base_goerli",
  "optimism_goerli",
  "klaytn_baobab",
  "celo_alfajores",
  "kava_testnet",
  "metis_goerli",
  "harmony_testnet",
  "zkSync_testnet",
  "aurora_testnet",
  "moonbase",
  "zkSync_era_testnet",
  "fuse_testnet",
  "linea_testnet",
  "scroll_testnet",
  "holesky",
  "polygon_amoy",
  "base_sepolia",
  "optimism_sepolia",
  "arbitrum_sepolia",
  "linea_sepolia",
  "blast_sepolia",
  "mantle_sepolia",
  "zksync_sepolia",
  "polygon_zkevm_cardona",
  "fantom_testnet",
  "lisk_sepolia",
  "flow_testnet",
  "ronin_saigon",
  "zetachain_testnet",
  "sei_testnet"
];
var LAYER1_CHAINS = [
  "eth",
  "etc",
  "bsc",
  "avalanche",
  "fantom",
  "klaytn",
  "celo",
  "kava",
  "harmony",
  "moonriver",
  "moonbeam",
  "fuse",
  "pulsechain",
  "flow",
  "ronin",
  "sei",
  "monad",
  "zetachain"
];
var LAYER2_CHAINS = [
  "polygon",
  "arbitrum",
  "optimism",
  "metis",
  "base",
  "zkSync_era",
  "linea",
  "scroll",
  "mantle",
  "opbnb",
  "polygon_zkevm",
  "blast",
  "lisk"
];
var SIDECHAINS = ["palm", "gnosis", "cronos", "aurora"];
var CHAIN_CONFIG = {
  "0x1": { token: "ETH", gasUnit: "Gwei", name: "Ethereum" },
  "0x3d": { token: "ETC", gasUnit: "Gwei", name: "Ethereum Classic" },
  "0x89": { token: "MATIC", gasUnit: "Gwei", name: "Polygon" },
  "0x38": { token: "BNB", gasUnit: "Gwei", name: "BNB Chain" },
  "0xa4b1": { token: "ARB", gasUnit: "Gwei", name: "Arbitrum" },
  "0x2105": { token: "ETH", gasUnit: "Gwei", name: "Base" },
  "0xa": { token: "ETH", gasUnit: "Gwei", name: "Optimism" },
  "0xe708": { token: "ETH", gasUnit: "Gwei", name: "Linea" },
  "0xa86a": { token: "AVAX", gasUnit: "nAVAX", name: "Avalanche" },
  "0xfa": { token: "FTM", gasUnit: "Gwei", name: "Fantom" },
  "0x19": { token: "CRO", gasUnit: "Gwei", name: "Cronos" },
  "0x64": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis" },
  "0x15b38": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz" },
  "0x171": { token: "PLS", gasUnit: "Gwei", name: "PulseChain" },
  "0x504": { token: "GLMR", gasUnit: "Gwei", name: "Moonbeam" },
  "0x505": { token: "MOVR", gasUnit: "Gwei", name: "Moonriver" },
  "0x13e31": { token: "ETH", gasUnit: "Gwei", name: "Blast" },
  "0x144": { token: "ETH", gasUnit: "Gwei", name: "zkSync Era" },
  "0x1388": { token: "MNT", gasUnit: "Gwei", name: "Mantle" },
  "0xcc": { token: "BNB", gasUnit: "Gwei", name: "opBNB" },
  // Gas on Polygon zkEVM is ETH, not MATIC.
  "0x44d": { token: "ETH", gasUnit: "Gwei", name: "Polygon zkEVM" },
  "0x1b58": { token: "ZETA", gasUnit: "Gwei", name: "ZetaChain" },
  "0x82750": { token: "ETH", gasUnit: "Gwei", name: "Scroll" },
  "0x4e454152": { token: "ETH", gasUnit: "Gwei", name: "Aurora" },
  "0x531": { token: "SEI", gasUnit: "Gwei", name: "Sei" },
  "0x8f": { token: "MON", gasUnit: "Gwei", name: "Monad" },
  "0x3e9": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn Baobab" },
  "0x4e454153": { token: "ETH", gasUnit: "Gwei", name: "Aurora Testnet" },
  "0x8274f": { token: "ETH", gasUnit: "Gwei", name: "Scroll Sepolia" },
  "0x530": { token: "SEI", gasUnit: "Gwei", name: "Sei Testnet" },
  "0x2eb": { token: "FLOW", gasUnit: "FLOW units", name: "Flow" },
  "0x7e4": { token: "RON", gasUnit: "Gwei", name: "Ronin" },
  "0x46f": { token: "LSK", gasUnit: "LSK units", name: "Lisk" },
  "0xaa36a7": { token: "ETH", gasUnit: "Gwei", name: "Sepolia" },
  "0x4268": { token: "ETH", gasUnit: "Gwei", name: "Holesky" },
  "0x13882": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Amoy" },
  "0x61": { token: "tBNB", gasUnit: "Gwei", name: "BSC Testnet" },
  "0x66eee": { token: "ARB", gasUnit: "Gwei", name: "Arbitrum Sepolia" },
  "0x14a34": { token: "ETH", gasUnit: "Gwei", name: "Base Sepolia" },
  "0xaa37dc": { token: "ETH", gasUnit: "Gwei", name: "Optimism Sepolia" },
  "0xe705": { token: "ETH", gasUnit: "Gwei", name: "Linea Sepolia" },
  "0xfa2": { token: "FTM", gasUnit: "Gwei", name: "Fantom Testnet" },
  "0x27d8": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis Chiado" },
  "0x15b32": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz Testnet" },
  "0x507": { token: "DEV", gasUnit: "Gwei", name: "Moonbase" },
  "0xa0c71fd": { token: "ETH", gasUnit: "Gwei", name: "Blast Sepolia" },
  "0x12c": { token: "ETH", gasUnit: "Gwei", name: "zkSync Sepolia" },
  "0x138b": { token: "MNT", gasUnit: "Gwei", name: "Mantle Sepolia" },
  "0x98a": { token: "MATIC", gasUnit: "Gwei", name: "Polygon zkEVM Cardona" },
  "0x1b59": { token: "ZETA", gasUnit: "Gwei", name: "Zetachain Testnet" },
  "0x221": { token: "FLOW", gasUnit: "FLOW units", name: "Flow Testnet" },
  "0x7e5": { token: "RON", gasUnit: "Gwei", name: "Ronin Saigon Testnet" },
  "0x106a": { token: "LSK", gasUnit: "LSK units", name: "Lisk Sepolia" },
  "0x0": { token: "BTC", gasUnit: "Sats/byte", name: "Bitcoin" },
  "0x1399c": { token: "SOL", gasUnit: "Lamports", name: "Solana" },
  "0x162": { token: "DOT", gasUnit: "Plancks", name: "Polkadot" },
  "0x2611": { token: "XPL", gasUnit: "Gwei", name: "Plasma Mainnet" }
};
var EXPLORER_URLS = {
  eth: "https://etherscan.io",
  etc: "https://blockscout.com/etc/mainnet",
  polygon: "https://polygonscan.com",
  bsc: "https://bscscan.com",
  avalanche: "https://snowtrace.io",
  fantom: "https://ftmscan.com",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  zksync_era: "https://explorer.zksync.io",
  base: "https://basescan.org",
  linea: "https://lineascan.build",
  scroll: "https://scrollscan.com",
  mantle: "https://explorer.mantle.xyz",
  opbnb: "https://opbnbscan.com",
  polygon_zkevm: "https://zkevm.polygonscan.com",
  blast: "https://blastscan.io",
  pulsechain: "https://scan.pulsechain.com",
  aurora: "https://explorer.aurora.dev",
  flow: "https://evm.flowscan.io",
  ronin: "https://app.roninchain.com",
  lisk: "https://blockscout.lisk.com",
  zetachain: "https://explorer.zetachain.com",
  sei: "https://seitrace.com",
  monad: "https://monadexplorer.com",
  goerli: "https://goerli.etherscan.io",
  sepolia: "https://sepolia.etherscan.io",
  mumbai: "https://mumbai.polygonscan.com",
  plasma: "https://plasmascan.to"
};
var TESTNET_TO_MAINNET = {
  goerli: "eth",
  sepolia: "eth",
  mumbai: "polygon",
  bsc_testnet: "bsc",
  gnosis_chiado: "gnosis",
  chiliz_spicy: "chiliz",
  base_goerli: "base",
  optimism_goerli: "optimism",
  klaytn_baobab: "klaytn",
  celo_alfajores: "celo",
  kava_testnet: "kava",
  metis_goerli: "metis",
  harmony_testnet: "harmony",
  zkSync_testnet: "zkSync_era",
  aurora_testnet: "aurora",
  moonbase: "moonbeam",
  zkSync_era_testnet: "zkSync_era",
  fuse_testnet: "fuse",
  linea_testnet: "linea",
  scroll_testnet: "scroll",
  holesky: "eth",
  polygon_amoy: "polygon",
  base_sepolia: "base",
  optimism_sepolia: "optimism",
  arbitrum_sepolia: "arbitrum",
  linea_sepolia: "linea",
  blast_sepolia: "blast",
  mantle_sepolia: "mantle",
  zksync_sepolia: "zkSync_era",
  polygon_zkevm_cardona: "polygon_zkevm",
  fantom_testnet: "fantom",
  lisk_sepolia: "lisk",
  flow_testnet: "flow",
  ronin_saigon: "ronin",
  zetachain_testnet: "zetachain",
  sei_testnet: "sei"
};

// src/chains/registry.ts
var ChainRegistry = class {
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  byCaip2 = /* @__PURE__ */ new Map();
  register(chain, { replace = false } = {}) {
    const existingSlug = this.bySlug.get(chain.slug);
    if (existingSlug && existingSlug !== chain && !replace) {
      throw new Error(
        `Chain slug "${chain.slug}" already registered; pass replace=true to overwrite.`
      );
    }
    if (chain.chainIdHex) {
      const existingHex = this.byHex.get(chain.chainIdHex);
      if (existingHex && existingHex.slug !== chain.slug && !replace) {
        throw new Error(
          `Chain hex ${chain.chainIdHex} already mapped to slug ${existingHex.slug}; pass replace=true to overwrite.`
        );
      }
    }
    this.bySlug.set(chain.slug, chain);
    if (chain.chainIdHex) this.byHex.set(chain.chainIdHex, chain);
    if (chain.caip2) this.byCaip2.set(chain.caip2, chain);
  }
  unregister(slug) {
    const chain = this.bySlug.get(slug);
    if (!chain) return null;
    this.bySlug.delete(slug);
    if (chain.chainIdHex) this.byHex.delete(chain.chainIdHex);
    if (chain.caip2) this.byCaip2.delete(chain.caip2);
    return chain;
  }
  clear() {
    this.bySlug.clear();
    this.byHex.clear();
    this.byCaip2.clear();
  }
  get(slug) {
    return this.bySlug.get(slug) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes + lowercases). */
  getByHex(chainIdHex) {
    let normalized = chainIdHex.toLowerCase();
    if (!normalized.startsWith("0x")) normalized = "0x" + normalized.replace(/^0x/, "");
    return this.byHex.get(normalized) ?? null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"). */
  getByCaip2(caip2) {
    return this.byCaip2.get(caip2.trim().toLowerCase()) ?? null;
  }
  /** Resolve by CAIP-2, hex, OR slug. */
  resolve(value) {
    if (!value) return null;
    if (value.includes(":")) {
      const hit = this.getByCaip2(value);
      if (hit) return hit;
    }
    if (value.startsWith("0x")) {
      const hit = this.getByHex(value);
      if (hit) return hit;
    }
    if (/^\d+$/.test(value)) {
      const hit = this.getByHex("0x" + BigInt(value).toString(16));
      if (hit) return hit;
    }
    return this.get(value.toLowerCase());
  }
  byFamily(family) {
    return [...this.bySlug.values()].filter((c) => c.family === family).sort((a, b) => a.slug.localeCompare(b.slug));
  }
  all() {
    return [...this.bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
  }
};
var registry = new ChainRegistry();
var REORG_DEPTHS_BY_SLUG = {
  eth: 12,
  polygon: 256,
  matic: 256,
  bsc: 15,
  arbitrum: 12,
  optimism: 12,
  base: 12,
  avalanche: 12,
  fantom: 12,
  cronos: 12,
  gnosis: 12,
  linea: 12,
  scroll: 12,
  mantle: 12,
  zksync: 12,
  zksync_era: 12,
  metis: 12,
  celo: 12,
  klaytn: 12,
  moonbeam: 12,
  moonriver: 12,
  harmony: 12,
  aurora: 12,
  fuse: 12,
  palm: 12,
  chiliz: 12,
  kava: 12,
  blast: 12,
  bitcoin: 6,
  solana: 32
};
var BLOCK_TIME_BY_SLUG = {
  eth: 12,
  etc: 13,
  polygon: 2,
  matic: 2,
  bsc: 3,
  arbitrum: 0.25,
  optimism: 2,
  base: 2,
  avalanche: 2,
  fantom: 1,
  cronos: 6,
  gnosis: 5,
  linea: 3,
  scroll: 3,
  mantle: 2,
  zksync: 1,
  zksync_era: 1,
  metis: 4,
  celo: 5,
  klaytn: 1,
  moonbeam: 12,
  moonriver: 12,
  harmony: 2,
  aurora: 1,
  fuse: 5,
  blast: 2,
  bitcoin: 600,
  solana: 0.4,
  polkadot: 6
};
var EXPLORER_SUPPLEMENT = {
  bitcoin: "https://blockstream.info",
  solana: "https://solscan.io",
  polkadot: "https://polkadot.subscan.io",
  cronos: "https://cronoscan.com",
  gnosis: "https://gnosisscan.io",
  celo: "https://celoscan.io",
  moonbeam: "https://moonscan.io",
  zkSync_era: "https://explorer.zksync.io",
  matic: "https://polygonscan.com"
};
function explorerFor(slug, rawSlug, mainnetSlug) {
  return EXPLORER_URLS[slug] ?? EXPLORER_URLS[rawSlug] ?? EXPLORER_SUPPLEMENT[slug] ?? EXPLORER_SUPPLEMENT[rawSlug] ?? (mainnetSlug ? EXPLORER_URLS[mainnetSlug] ?? EXPLORER_SUPPLEMENT[mainnetSlug] ?? null : null);
}
var BRAND_COLOR_BY_SLUG = {
  eth: "#627EEA",
  etc: "#328332",
  polygon: "#8247E5",
  matic: "#8247E5",
  bsc: "#F0B90B",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  base: "#0052FF",
  avalanche: "#E84142",
  fantom: "#1969FF",
  cronos: "#002D74",
  gnosis: "#0B6E4F",
  linea: "#61DFFF",
  scroll: "#EBC28E",
  mantle: "#000000",
  zksync: "#8C8DFC",
  zksync_era: "#8C8DFC",
  metis: "#00DACC",
  celo: "#FCFF52",
  klaytn: "#FF1B1B",
  moonbeam: "#53CBC8",
  moonriver: "#C0C0C0",
  harmony: "#00AEE9",
  aurora: "#70D44B",
  fuse: "#B4F9BA",
  blast: "#FCFC03",
  kava: "#FF433E",
  bitcoin: "#F7931A",
  solana: "#14F195",
  polkadot: "#E6007A"
};
function brandColorFor(slug, mainnetSlug) {
  return BRAND_COLOR_BY_SLUG[slug] ?? (mainnetSlug ? BRAND_COLOR_BY_SLUG[mainnetSlug] ?? null : null);
}
var LOGO_URL_BY_SLUG = {};
var CAIP2_BY_SLUG = {
  bitcoin: "bip122:000000000019d6689c085ae165831e93",
  solana: "solana:5eykt4usfv8p8njdtrepy1vzqkqzkvdp",
  polkadot: "polkadot:91b171bb158e2d3848fa23a9f1c25182"
};
function deriveCaip2(slug, family, chainIdHex) {
  if (CAIP2_BY_SLUG[slug]) return CAIP2_BY_SLUG[slug];
  if (family === ChainFamily.EVM && chainIdHex) {
    return `eip155:${BigInt(chainIdHex).toString(10)}`;
  }
  return null;
}
var NON_EVM_OVERRIDES = {
  bitcoin: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null },
  solana: { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null },
  polkadot: { family: ChainFamily.OTHER, decimals: 10, chainIdHex: null }
};
function layerFor(slug) {
  if (LAYER1_CHAINS.includes(slug)) return "l1";
  if (LAYER2_CHAINS.includes(slug)) return "l2";
  if (SIDECHAINS.includes(slug)) return "sidechain";
  return "unknown";
}
function seedFromCanonical() {
  for (const [hexId, rawSlug] of Object.entries(CHAIN_MAPPING)) {
    const slug = rawSlug.toLowerCase();
    const config2 = CHAIN_CONFIG[hexId];
    const token = config2?.token ?? "ETH";
    const override = NON_EVM_OVERRIDES[slug];
    const family = override?.family ?? ChainFamily.EVM;
    const decimals = override?.decimals ?? 18;
    const isTestnet = TESTNET_CHAINS.includes(rawSlug);
    let mainnetSlug = null;
    if (isTestnet) {
      const m = TESTNET_TO_MAINNET[rawSlug];
      if (m) mainnetSlug = m.toLowerCase();
    }
    const reorgDepth = REORG_DEPTHS_BY_SLUG[slug] ?? 12;
    const chainIdHex = hexId.toLowerCase();
    const metadata = { layer: layerFor(slug) };
    if (config2?.gasUnit) metadata.gasUnit = config2.gasUnit;
    if (slug === "etc") {
      metadata.fork_of = "eth";
      metadata.note = "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk.";
    }
    try {
      registry.register(
        {
          slug,
          chainIdHex,
          family,
          name: config2?.name ?? rawSlug,
          nativeToken: token,
          decimals,
          isTestnet,
          mainnetSlug,
          reorgDepth,
          explorerUrl: explorerFor(slug, rawSlug, mainnetSlug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? (mainnetSlug ? BLOCK_TIME_BY_SLUG[mainnetSlug] ?? null : null),
          caip2: deriveCaip2(slug, family, chainIdHex),
          brandColor: brandColorFor(slug, mainnetSlug),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? (mainnetSlug ? LOGO_URL_BY_SLUG[mainnetSlug] ?? null : null),
          metadata
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
  for (const [slug, ovr] of Object.entries(NON_EVM_OVERRIDES)) {
    if (registry.get(slug)) continue;
    const nativeToken = slug === "bitcoin" ? "BTC" : slug === "solana" ? "SOL" : slug === "polkadot" ? "DOT" : "?";
    try {
      registry.register(
        {
          slug,
          chainIdHex: ovr.chainIdHex,
          family: ovr.family,
          name: slug[0].toUpperCase() + slug.slice(1),
          nativeToken,
          decimals: ovr.decimals,
          isTestnet: false,
          mainnetSlug: null,
          reorgDepth: REORG_DEPTHS_BY_SLUG[slug] ?? 12,
          explorerUrl: explorerFor(slug, slug, null),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? null,
          caip2: deriveCaip2(slug, ovr.family, ovr.chainIdHex),
          brandColor: brandColorFor(slug, null),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? null,
          metadata: {}
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
}
seedFromCanonical();

// src/chains/address.ts
var RE_EVM = /^0x[0-9a-fA-F]{40}$/;
var RE_BTC_BASE58 = /^(1|3)[1-9A-HJ-NP-Za-km-z]{25,39}$/;
var RE_BTC_BECH32 = /^(bc1|tb1)[0-9ac-hj-np-z]{11,71}$/;
var RE_SOLANA = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
var RE_COSMOS_BECH32 = /^[a-z][a-z0-9]{1,10}1[0-9ac-hj-np-z]{38,58}$/;
function addressFamily(address, chainId) {
  if (RE_EVM.test(address)) return ChainFamily.EVM;
  if (RE_BTC_BASE58.test(address) || RE_BTC_BECH32.test(address)) return ChainFamily.UTXO;
  if (RE_SOLANA.test(address)) return ChainFamily.SOLANA;
  if (RE_COSMOS_BECH32.test(address)) return ChainFamily.COSMOS;
  return null;
}
function isValidAddress(address, chainId) {
  const addr = address.trim();
  if (!addr) return false;
  const fam = addressFamily(addr);
  switch (fam) {
    case ChainFamily.EVM:
      return RE_EVM.test(addr);
    case ChainFamily.UTXO:
      return RE_BTC_BASE58.test(addr) || RE_BTC_BECH32.test(addr);
    case ChainFamily.SOLANA:
      return RE_SOLANA.test(addr);
    case ChainFamily.COSMOS:
      return RE_COSMOS_BECH32.test(addr);
    default:
      return RE_EVM.test(addr) || RE_BTC_BASE58.test(addr) || RE_BTC_BECH32.test(addr) || RE_SOLANA.test(addr) || RE_COSMOS_BECH32.test(addr);
  }
}

// src/storage/tx-body.ts
var ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
var TRANSFER_KINDS = {
  native_transfer: "native",
  erc20_transfer: "erc20",
  nft_transfer: "nft"
};
function looksLikeAddress(value) {
  return typeof value === "string" && ADDRESS_RE.test(value);
}
function rec(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function s(value) {
  return value == null || value === "" ? void 0 : String(value);
}
function addrStr(value) {
  if (typeof value === "string") return value || void 0;
  const o = rec(value);
  if (!o) return void 0;
  return s(o.value) ?? s(o.address) ?? s(o.hash);
}
var DECODED_ENVELOPE_MARKER = "@decoded";
function isEnriched(value) {
  const o = rec(value);
  return !!o && !!rec(o[DECODED_ENVELOPE_MARKER]);
}
function displayOf(value) {
  const inner = isEnriched(value) ? rec(value[DECODED_ENVELOPE_MARKER]) : void 0;
  return inner ? inner.display : value;
}
function rawOf(value) {
  const inner = isEnriched(value) ? rec(value[DECODED_ENVELOPE_MARKER]) : void 0;
  return inner ? inner.raw : value;
}
function decodedOf(value) {
  const inner = isEnriched(value) ? rec(value[DECODED_ENVELOPE_MARKER]) : void 0;
  return inner ? inner.decoded : void 0;
}
function kindOf(value) {
  const inner = isEnriched(value) ? rec(value[DECODED_ENVELOPE_MARKER]) : void 0;
  return inner ? s(inner.kind) : void 0;
}
function params(value) {
  if (!Array.isArray(value)) return [];
  return value.map((p) => {
    const o = rec(p) ?? {};
    const display = "display" in o ? o.display : isEnriched(o.value) ? displayOf(o.value) : void 0;
    const semanticType = s(o.semantic_type) ?? kindOf(o.value);
    return {
      name: s(o.name) ?? "",
      type: s(o.type) ?? "",
      value: o.value,
      display: display ?? void 0,
      semanticType,
      canonicalName: s(o.canonical_name) ?? void 0
    };
  });
}
function proxyChain(value) {
  return Array.isArray(value) ? value.map((v) => String(v)).filter(Boolean) : [];
}
function parseDecodedCallValue(value) {
  return toCall(value);
}
function toCall(value) {
  const o = rec(value);
  if (!o) return null;
  const label = s(o.label) ?? s(o.name) ?? s(o.function);
  const signature = s(o.signature);
  const address = addrStr(o.address) ?? addrStr(o.abi_address);
  const ps = params(o.params);
  const returnParams = Array.isArray(o.decoded_return) ? params(o.decoded_return) : params(
    rec(o.decoded_return)?.params ?? rec(rec(o.metadata)?.decoded_return)?.params ?? o.return_params ?? o.returns
  );
  if (!label && !signature && ps.length === 0 && returnParams.length === 0 && !address) return null;
  return {
    label,
    signature,
    address,
    source: s(o.source) ?? s(o.abi_source),
    params: ps,
    returnParams,
    proxyChain: proxyChain(o.proxy_chain)
  };
}
function decodeCarrier(body) {
  const root = rec(body) ?? {};
  return rec(root.canonical_transaction) ?? root;
}
function extras(body) {
  return rec(decodeCarrier(body).extras) ?? {};
}
function enrichmentPayload(body, kind) {
  const root = rec(body) ?? {};
  const layers = asArray(rec(root.extras)?.enrichment_layers) ?? asArray(root.enrichment_layers) ?? asArray(extras(body).enrichment_layers) ?? asArray(decodeCarrier(body).enrichment_layers);
  for (const layer of layers ?? []) {
    for (const e of asArray(rec(layer)?.enrichments) ?? []) {
      const eo = rec(e);
      if (eo && s(eo.kind) === kind) return rec(eo.payload);
    }
  }
  return void 0;
}
function decodedTx(body) {
  return enrichmentPayload(body, "decoded_transaction");
}
function decodedEventsByIndex(body) {
  const out = /* @__PURE__ */ new Map();
  for (const ev of asArray(decodedTx(body)?.decoded_events) ?? []) {
    const o = rec(ev);
    const idx = numOrUndef(o?.log_index);
    if (o && idx !== void 0 && !out.has(idx)) out.set(idx, o);
  }
  return out;
}
function decodedInternalsByTrace(body) {
  const out = /* @__PURE__ */ new Map();
  for (const itx of asArray(decodedTx(body)?.decoded_internal_txs) ?? []) {
    const o = rec(itx);
    const trace = asArray(o?.trace_address);
    const key = trace && trace.length > 0 ? numOrUndef(trace[0]) : void 0;
    if (o && key !== void 0 && !out.has(key)) out.set(key, o);
  }
  return out;
}
function allCanonicalEvents(body) {
  const list = asArray(decodeCarrier(body).events) ?? [];
  return list.map((e) => rec(e)).filter((e) => !!e);
}
function eventsOfKind(body, kind) {
  return allCanonicalEvents(body).filter((e) => s(e.kind) === kind);
}
function rawProviderPayload(body) {
  const root = rec(body) ?? {};
  const rpp = rec(root.raw_provider_payload);
  return rec(rpp?.payload) ?? rec(root.raw_provider_payload) ?? {};
}
function numOrUndef(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : void 0;
}
function byIndex(a, b) {
  return (a.index ?? 0) - (b.index ?? 0);
}
function asArray(value) {
  return Array.isArray(value) ? value : void 0;
}
var STATUS_SUCCESS = /* @__PURE__ */ new Set(["success", "succeeded", "ok", "confirmed", "1", "true"]);
var STATUS_FAILED = /* @__PURE__ */ new Set(["failed", "failure", "reverted", "revert", "error", "0", "false"]);
function coerceTxStatus(parts) {
  const raw = parts.status != null && parts.status !== "" ? String(parts.status) : void 0;
  const receiptStatus = numOrUndef(parts.receiptStatus);
  const error = parts.error != null && parts.error !== "" ? String(parts.error) : void 0;
  let kind = "unknown";
  if (receiptStatus === 1) kind = "success";
  else if (receiptStatus === 0) kind = "failed";
  else {
    const r = raw?.trim().toLowerCase();
    if (r && STATUS_SUCCESS.has(r)) kind = "success";
    else if (r && STATUS_FAILED.has(r)) kind = "failed";
    else if (r === "pending") kind = "pending";
    else if (error) kind = "failed";
  }
  return { kind, raw, receiptStatus, error };
}
function parseTxStatus(body) {
  const carrier = decodeCarrier(body);
  const dt = decodedTx(body) ?? {};
  return coerceTxStatus({
    status: s(carrier.status) ?? s(dt.status),
    receiptStatus: numOrUndef(carrier.receipt_status) ?? numOrUndef(dt.receipt_status),
    error: s(carrier.error) ?? s(dt.error)
  });
}
function parseDecodedCall(body) {
  const carrier = decodeCarrier(body);
  const root = rec(body) ?? {};
  const wrap = rec(root.decoded_transaction);
  return (
    // Standard `decoded_transaction` enrichment — the single source of truth.
    toCall(decodedTx(body)?.decoded_call) ?? // Legacy: inlined on the body / Moralis row.
    toCall(extras(body).decoded_call) ?? toCall(carrier.decoded_call) ?? toCall(wrap?.decoded_call) ?? toCall(root.decoded_call)
  );
}
function topicsFrom(o) {
  if (Array.isArray(o.topics)) return o.topics.map((t) => String(t)).filter(Boolean);
  const out = [];
  for (const k of ["topic0", "topic1", "topic2", "topic3"]) {
    const v = s(o[k]);
    if (v) out.push(v);
  }
  return out;
}
function logFromCanonical(e, std) {
  const raw = rec(e.raw_payload) ?? {};
  const decoded = std ?? rec(e.decoded);
  return {
    index: numOrUndef(e.log_index ?? decoded?.log_index ?? raw.log_index),
    address: addrStr(e.address) ?? addrStr(decoded?.address) ?? addrStr(raw.address),
    name: s(decoded?.event) ?? s(decoded?.name),
    signature: s(decoded?.signature),
    source: s(decoded?.source) ?? s(decoded?.abi_source),
    params: params(decoded?.params),
    topics: topicsFrom(raw),
    data: s(raw.data),
    raw: !decoded
  };
}
function logFromStandard(o) {
  return {
    index: numOrUndef(o.log_index),
    address: addrStr(o.address),
    name: s(o.event) ?? s(o.name),
    signature: s(o.signature),
    source: s(o.abi_source) ?? s(o.source),
    params: params(o.params),
    topics: [],
    data: void 0,
    raw: false
  };
}
function logFromLegacy(o) {
  const decoded = rec(o.decoded_event);
  return {
    index: numOrUndef(o.log_index),
    address: addrStr(o.address) ?? addrStr(decoded?.address),
    name: s(decoded?.name) ?? s(decoded?.label),
    signature: s(decoded?.signature),
    source: s(decoded?.source),
    params: params(decoded?.params),
    topics: topicsFrom(o),
    data: s(o.data),
    raw: !decoded
  };
}
function parseEvents(body) {
  const canonical = eventsOfKind(body, "log");
  if (canonical.length > 0) {
    const std2 = decodedEventsByIndex(body);
    return canonical.map((e) => {
      const idx = numOrUndef(e.log_index);
      return logFromCanonical(e, idx !== void 0 ? std2.get(idx) : void 0);
    }).sort(byIndex);
  }
  const std = asArray(decodedTx(body)?.decoded_events);
  if (std && std.length > 0) {
    return std.map((e) => logFromStandard(rec(e) ?? {})).sort(byIndex);
  }
  const root = rec(body) ?? {};
  const wrap = rec(root.decoded_transaction);
  const legacy = asArray(wrap?.decoded_events) ?? asArray(root.logs) ?? asArray(rawProviderPayload(body).logs) ?? [];
  return legacy.map((l) => logFromLegacy(rec(l) ?? {})).sort(byIndex);
}
var CREATION_CALL_TYPES = /* @__PURE__ */ new Set(["create", "create2"]);
function isCreationCallType(callType) {
  if (typeof callType !== "string") return false;
  return CREATION_CALL_TYPES.has(callType.trim().toLowerCase().replace(/[_\s-]/g, ""));
}
function traceAddress(value) {
  return (asArray(value) ?? []).map((v) => numOrUndef(v)).filter((n) => n !== void 0);
}
function creationFields(callType, source, to) {
  const isCreation = isCreationCallType(callType);
  const candidate = addrStr(source?.deployed_address) ?? (isCreation ? to : void 0);
  return {
    isCreation,
    deployedAddress: candidate && isValidAddress(candidate) ? candidate : void 0,
    deployedCodeHash: s(source?.deployed_code_hash),
    deployedContractType: s(source?.deployed_contract_type),
    deployedProtocolId: s(source?.deployed_protocol_id)
  };
}
function internalFromCanonical(e, i, std) {
  const raw = rec(e.raw_payload) ?? {};
  const decoded = std ?? rec(e.decoded);
  const inputCall = toCall(decoded?.call) ?? toCall(decoded?.input) ?? toCall(decoded);
  const callType = s(raw.type ?? raw.call_type) ?? s(decoded?.call_type);
  const to = addrStr(e.address) ?? addrStr(raw.to ?? raw.to_address) ?? addrStr(decoded?.to_address) ?? addrStr(decoded?.address);
  return {
    index: numOrUndef(e.internal_tx_index ?? decoded?.internal_tx_index) ?? i,
    from: addrStr(raw.from ?? raw.from_address) ?? addrStr(decoded?.from_address),
    to,
    value: s(raw.value ?? raw.amount) ?? s(decoded?.value),
    call: inputCall,
    callType,
    traceAddress: traceAddress(decoded?.trace_address ?? raw.trace_address),
    ...creationFields(callType, decoded, to)
  };
}
function internalFromLegacy(o, i) {
  const callType = s(o.type ?? o.call_type);
  const to = addrStr(o.to ?? o.to_address);
  return {
    index: numOrUndef(o.internal_tx_index) ?? i,
    from: addrStr(o.from ?? o.from_address),
    to,
    value: s(o.value ?? o.amount),
    // `.call` is the standard `DecodedInternalTx` shape; `.input`/`.decoded_call`
    // are the older wrapped / Moralis shapes.
    call: toCall(o.call) ?? toCall(o.input) ?? toCall(o.decoded_call),
    callType,
    traceAddress: traceAddress(o.trace_address),
    ...creationFields(callType, o, to)
  };
}
function parseInternalTxs(body) {
  const canonical = eventsOfKind(body, "internal_tx");
  if (canonical.length > 0) {
    const std = decodedInternalsByTrace(body);
    return canonical.map((e, i) => {
      const idx = numOrUndef(e.internal_tx_index);
      return internalFromCanonical(e, i, idx !== void 0 ? std.get(idx) : void 0);
    }).sort(byIndex);
  }
  const root = rec(body) ?? {};
  const wrap = rec(root.decoded_transaction);
  const legacy = (
    // `decoded_internal_txs` is the standard field; `decoded_internal_transactions`
    // is the older wrapped shape.
    asArray(decodedTx(body)?.decoded_internal_txs) ?? asArray(wrap?.decoded_internal_txs) ?? asArray(wrap?.decoded_internal_transactions) ?? asArray(root.internal_transactions) ?? asArray(rawProviderPayload(body).internal_transactions) ?? []
  );
  return legacy.map((t, i) => internalFromLegacy(rec(t) ?? {}, i)).sort(byIndex);
}
function transferFrom(e, kind) {
  const raw = rec(e.raw_payload) ?? e;
  return {
    kind,
    from: addrStr(raw.from ?? raw.from_address),
    to: addrStr(raw.to ?? raw.to_address),
    value: s(raw.value ?? raw.amount),
    token: addrStr(raw.address ?? raw.token_address ?? raw.contract_address),
    symbol: s(raw.token_symbol ?? raw.symbol),
    tokenId: s(raw.token_id ?? raw.tokenId)
  };
}
function parseTransfers(body) {
  const out = [];
  for (const e of allCanonicalEvents(body)) {
    const kind = TRANSFER_KINDS[s(e.kind) ?? ""];
    if (kind) out.push(transferFrom(e, kind));
  }
  if (out.length > 0) return out;
  const payload = { ...rec(body) ?? {}, ...rawProviderPayload(body) };
  const pushAll = (list, kind) => {
    for (const t of asArray(list) ?? []) {
      const o = rec(t);
      if (o) out.push(transferFrom(o, kind));
    }
  };
  pushAll(payload.native_transfers, "native");
  pushAll(payload.erc20_transfers, "erc20");
  pushAll(payload.nft_transfers, "nft");
  return out;
}
function parseEntityHints(body) {
  const list = decodeCarrier(body).tx_entity_hints;
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const h of list) {
    const o = rec(h);
    const address = addrStr(o?.address);
    if (!o || !address) continue;
    out.push({
      address,
      entityName: s(o.entity_name),
      entityLogo: s(o.entity_logo),
      label: s(o.label),
      confidence: numOrUndef(o.confidence),
      source: s(o.source)
    });
  }
  return out;
}
function strList(value) {
  return asArray(value)?.map((v) => s(v)).filter((v) => !!v) ?? [];
}
function listOrUndef(value) {
  const list = strList(value);
  return list.length > 0 ? list : void 0;
}
function crossChainRef(value) {
  const o = rec(value);
  const id = o && s(o.value);
  if (!o || !id) return null;
  return {
    linkageType: s(o.linkage_type),
    idLabel: s(o.id_label),
    value: id,
    role: s(o.role),
    protocolId: s(o.protocol_id),
    sagaKind: s(o.saga_kind),
    source: s(o.source),
    relationshipType: s(o.relationship_type),
    linkageModel: s(o.linkage_model),
    confidence: numOrUndef(o.confidence),
    // Absent rather than empty when the protocol pins no completion set, so a
    // consumer can tell "declared nothing" from "declared an empty set" and fall
    // back to the canonical source/destination pair.
    completeRoles: listOrUndef(o.complete_roles)
  };
}
function renderHintsRaw(body) {
  const root = rec(body) ?? {};
  return rec(rec(root.extras)?.render_hints) ?? rec(root.render_hints) ?? rec(extras(body).render_hints);
}
function valueDisplayHint(value) {
  const o = rec(value);
  const kind = o && s(o.kind);
  const component = o && s(o.component);
  if (!o || !kind || !component) return null;
  return {
    protocolId: s(o.protocol_id),
    kind,
    component,
    label: s(o.label),
    icon: s(o.icon),
    fields: strList(o.fields),
    description: s(o.description)
  };
}
function eventViewHint(value) {
  const o = rec(value);
  const event = o && s(o.event);
  const component = o && s(o.component);
  if (!o || !event || !component) return null;
  return {
    protocolId: s(o.protocol_id),
    event,
    signature: s(o.signature),
    component,
    label: s(o.label),
    summaryFields: strList(o.summary_fields)
  };
}
function callViewHint(value) {
  const o = rec(value);
  const call = o && s(o.call);
  const component = o && s(o.component);
  if (!o || !call || !component) return null;
  return {
    protocolId: s(o.protocol_id),
    call,
    signature: s(o.signature),
    component,
    label: s(o.label),
    summaryFields: strList(o.summary_fields)
  };
}
function parseRenderHints(body) {
  const raw = renderHintsRaw(body);
  return {
    valueHints: (asArray(raw?.display_hints) ?? []).map(valueDisplayHint).filter((h) => h !== null),
    eventHints: (asArray(raw?.event_views) ?? []).map(eventViewHint).filter((h) => h !== null),
    callHints: (asArray(raw?.call_views) ?? []).map(callViewHint).filter((h) => h !== null)
  };
}
function keyAttribute(value) {
  const o = rec(value);
  const key = o && s(o.key);
  const label = o && s(o.label);
  if (!o || !key || !label) return null;
  const raw = o.value;
  if (raw === null || raw === void 0 || raw === "") return null;
  return {
    protocolId: s(o.protocol_id),
    key,
    label,
    kind: s(o.kind),
    source: s(o.source),
    value: raw,
    confidence: numOrUndef(o.confidence),
    icon: s(o.icon),
    description: s(o.description)
  };
}
function keyAttributesRaw(body) {
  const root = rec(body) ?? {};
  return asArray(rec(root.extras)?.key_attributes) ?? asArray(root.key_attributes) ?? asArray(extras(body).key_attributes) ?? [];
}
function keyAttributeGroup(value) {
  const o = rec(value);
  if (!o) return null;
  const rawAttrs = asArray(o.attributes) ?? [o];
  const attributes = rawAttrs.map(keyAttribute).filter((a) => a !== null);
  if (attributes.length === 0) return null;
  return {
    protocolId: s(o.protocol_id) ?? attributes[0]?.protocolId,
    entityType: s(o.entity_type),
    index: numOrUndef(o.index),
    eventName: s(o.event_name),
    attributes
  };
}
function parseKeyAttributeGroups(value) {
  return (asArray(value) ?? []).map(keyAttributeGroup).filter((g) => g !== null);
}
function parseKeyAttributes(body) {
  return parseKeyAttributeGroups(keyAttributesRaw(body));
}
function parseCrossChainRefs(body) {
  const dt = decodedTx(body) ?? {};
  return (asArray(dt.cross_chain_refs) ?? []).map(crossChainRef).filter((r) => r !== null);
}
function parseDecodeMeta(body) {
  const dt = decodedTx(body) ?? {};
  const meta = rec(dt.metadata) ?? {};
  return {
    primaryProtocolId: s(dt.primary_protocol_id),
    protocolIds: strList(dt.protocol_ids),
    crossChainRefs: parseCrossChainRefs(body),
    decoderName: s(dt.decoder_name),
    decoderVersion: s(dt.decoder_version),
    decodedAt: s(meta.decoded_at),
    durationMs: numOrUndef(dt.duration_ms),
    strategiesUsed: strList(dt.strategies_used),
    enrichmentSources: strList(dt.enrichment_sources),
    renderHints: parseRenderHints(body),
    keyAttributes: parseKeyAttributes(body)
  };
}
function collectAddresses(value, out) {
  if (looksLikeAddress(value)) {
    out.add(value);
    return;
  }
  if (isEnriched(value)) {
    const inner = rec(value[DECODED_ENVELOPE_MARKER]) ?? {};
    collectAddresses(inner.display, out);
    collectAddresses(inner.decoded, out);
    return;
  }
  if (Array.isArray(value)) {
    for (const v of value) collectAddresses(v, out);
    return;
  }
  const o = rec(value);
  if (o) for (const v of Object.values(o)) collectAddresses(v, out);
}
var ROLE_PRIORITY = [
  "from",
  "to",
  "call-target",
  "event-emitter",
  "transfer",
  "internal",
  "param"
];
function buildAddressIndex(parsed) {
  const roles = /* @__PURE__ */ new Map();
  const display = /* @__PURE__ */ new Map();
  const index = (addr, role) => {
    const key = addr.toLowerCase();
    if (!display.has(key)) display.set(key, addr);
    const entry = roles.get(key) ?? /* @__PURE__ */ new Set();
    entry.add(role);
    roles.set(key, entry);
  };
  const add = (addr, role) => {
    if (!looksLikeAddress(addr)) return;
    index(addr, role);
  };
  const addValidated = (addr, role) => {
    if (addr) index(addr, role);
  };
  const addParamAddrs = (ps) => {
    const found = /* @__PURE__ */ new Set();
    for (const p of ps) collectAddresses(p.value, found);
    for (const a of found) add(a, "param");
  };
  add(parsed.from, "from");
  add(parsed.to, "to");
  if (parsed.decodedCall) {
    add(parsed.decodedCall.address, "call-target");
    addParamAddrs(parsed.decodedCall.params);
    addParamAddrs(parsed.decodedCall.returnParams);
  }
  for (const ev of parsed.events) {
    add(ev.address, "event-emitter");
    addParamAddrs(ev.params);
  }
  for (const t of parsed.transfers) {
    add(t.from, "transfer");
    add(t.to, "transfer");
    add(t.token, "transfer");
  }
  for (const itx of parsed.internalTxs) {
    add(itx.from, "internal");
    add(itx.to, "internal");
    addValidated(itx.deployedAddress, "internal");
    if (itx.call) {
      add(itx.call.address, "call-target");
      addParamAddrs(itx.call.params);
      addParamAddrs(itx.call.returnParams);
    }
  }
  const rank = (rs) => {
    for (let i = 0; i < ROLE_PRIORITY.length; i += 1) {
      const role = ROLE_PRIORITY[i];
      if (role && rs.has(role)) return i;
    }
    return ROLE_PRIORITY.length;
  };
  return [...roles.entries()].map(([key, rs]) => ({ address: display.get(key) ?? key, roles: [...rs] })).sort((a, b) => rank(new Set(a.roles)) - rank(new Set(b.roles)));
}
function parseTxBody(body, fromHint, toHint) {
  const carrier = decodeCarrier(body);
  const from = fromHint ?? addrStr(carrier.from_address);
  const to = toHint ?? addrStr(carrier.to_address);
  const decodedCall = parseDecodedCall(body);
  const events = parseEvents(body);
  const internalTxs = parseInternalTxs(body);
  const transfers = parseTransfers(body);
  const entityHints = parseEntityHints(body);
  return {
    from,
    to,
    decodedCall,
    events,
    internalTxs,
    transfers,
    entityHints,
    addresses: buildAddressIndex({ from, to, decodedCall, events, internalTxs, transfers }),
    status: parseTxStatus(body),
    meta: parseDecodeMeta(body)
  };
}
var MediaRefSchema = z.object({
  kind: z.string().default("placeholder"),
  value: z.string(),
  fallback: z.union([z.string(), z.null()]).default(null),
  alt: z.union([z.string(), z.null()]).default(null)
});
var SceneAssetSchema = z.object({
  token_address: z.union([z.string(), z.null()]).default(null),
  token_chain_id: z.union([z.string(), z.null()]).default(null),
  symbol: z.union([z.string(), z.null()]).default(null),
  decimals: z.union([z.number().int(), z.null()]).default(null),
  amount_raw: z.union([z.string(), z.null()]).default(null),
  amount: z.union([z.string(), z.null()]).default(null),
  usd: z.union([z.string(), z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  // 'actual' | 'quoted_minimum' — mirrors commons AmountBasis. Lets a label
  // flag a swap min-return floor as "(min)" instead of an exact amount.
  basis: z.union([z.string(), z.null()]).default(null),
  min_amount_raw: z.union([z.string(), z.null()]).default(null),
  min_amount: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
var SceneSourceRefSchema = z.object({
  ref_type: z.string(),
  address: z.union([z.string(), z.null()]).default(null),
  log_index: z.union([z.number().int(), z.null()]).default(null),
  internal_tx_index: z.union([z.number().int(), z.null()]).default(null),
  path: z.array(z.string()).default([])
});
var SceneFactSchema = z.object({
  key: z.string(),
  label: z.string().default(""),
  kind: z.union([z.string(), z.null()]).default(null),
  value: z.unknown(),
  protocol_id: z.union([z.string(), z.null()]).default(null),
  source: z.union([z.string(), z.null()]).default(null),
  path_id: z.union([z.string(), z.null()]).default(null),
  confidence: z.union([z.number().min(0).max(1), z.null()]).default(null),
  icon: z.union([z.string(), z.null()]).default(null),
  description: z.union([z.string(), z.null()]).default(null)
});
var SceneNodeSchema = z.object({
  id: z.string(),
  kind: z.string().default("address"),
  label: z.string().default(""),
  sublabel: z.union([z.string(), z.null()]).default(null),
  chain_id: z.union([z.string(), z.null()]).default(null),
  address: z.union([z.string(), z.null()]).default(null),
  group_id: z.union([z.string(), z.null()]).default(null),
  off_chain: z.boolean().default(false),
  entity_id: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  assets: z.array(SceneAssetSchema).default([]),
  value: z.union([z.string(), z.null()]).default(null),
  badge: z.union([z.string(), z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  confidence: z.number().min(0).max(1).default(1),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  kind: z.string().default("flow"),
  label: z.union([z.string(), z.null()]).default(null),
  asset: z.union([SceneAssetSchema, z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  from_chain: z.union([z.string(), z.null()]).default(null),
  to_chain: z.union([z.string(), z.null()]).default(null),
  cross_chain: z.boolean().default(false),
  virtual: z.boolean().default(false),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneGroupSchema = z.object({
  id: z.string(),
  group_type: z.string().default("chain"),
  chain_id: z.union([z.string(), z.null()]).default(null),
  label: z.string().default(""),
  member_node_ids: z.array(z.string()).default([]),
  color: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
var GraphSceneSchema = z.object({
  version: z.string().default("1"),
  kind: z.string().default("semantic"),
  summary: z.union([z.string(), z.null()]).default(null),
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  // The transaction's headline "business metrics" — the SAME resolved
  // `key_attributes` groups the detail-page highlights panel shows, carried on
  // EVERY chart kind so the graph view renders a consistent highlights strip.
  // Kept as the raw snake_case wire shape (a passthrough) so the wire layer
  // stays below `storage`; a consumer normalises it with `parseKeyAttributeGroups`
  // (the same parser the detail panel + group view use) → `KeyAttributeGroup[]`.
  highlights: z.array(z.record(z.string(), z.unknown())).default([]),
  chain_ids: z.array(z.string()).default([]),
  multi_chain: z.boolean().default(false),
  layout_hint: z.string().default("hierarchical"),
  // Compact-first: mirrors the backend `GraphBuildPolicy.default_view_tier`
  // default so a scene missing the field opens on the minimal intent + highlights
  // (the user raises the tier live). The scene always carries every tier.
  default_detail_tier: z.string().default("compact"),
  attributes: z.record(z.string(), z.unknown()).default({})
});
function parseGraphScene(value) {
  return GraphSceneSchema.parse(value);
}
z.object({
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  hidden_node_ids: z.array(z.string()).default([]),
  hidden_edge_ids: z.array(z.string()).default([]),
  node_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  edge_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({})
});
var GROUPING_DIMENSIONS = [
  "chain",
  "protocol",
  "entity",
  "tag",
  "label",
  "kind",
  "saga"
];
var GroupingDimensionOptionsSchema = z.object({
  /** Drop a band with fewer members than this (a band of one is just a node). */
  min_members: z.number().int().positive().optional(),
  /** Keep at most this many bands (largest first); the rest go ungrouped. */
  max_bands: z.number().int().positive().optional()
});
z.object({
  dimensions: z.array(z.enum(GROUPING_DIMENSIONS)).default([]),
  options: z.record(z.string(), GroupingDimensionOptionsSchema).default({})
});

// src/storage/group-view.ts
function rec2(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function str(value) {
  return value == null || value === "" ? void 0 : String(value);
}
function parseLeg(value) {
  const o = rec2(value);
  const role = o && str(o.role);
  const chainId = o && (str(o.chain_id) ?? str(o.chainId));
  if (!o || !role || !chainId) return null;
  return {
    role,
    chainId,
    txHash: str(o.tx_hash) ?? str(o.txHash),
    label: str(o.label)
  };
}
function parseLegs(value) {
  return (Array.isArray(value) ? value : []).map(parseLeg).filter((l) => l !== null);
}
function strList2(value) {
  if (!Array.isArray(value)) return [];
  return value.map((v) => str(v)).filter((v) => v != null);
}
function num(value) {
  if (value == null || value === "") return void 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : void 0;
}
function parseGroupView(value) {
  const root = rec2(value) ?? {};
  const scene = parseGraphScene(root.scene ?? root);
  const attrs = scene.attributes ?? {};
  const group = rec2(root.group) ?? {};
  const highlights = parseKeyAttributeGroups(root.highlights ?? attrs.highlights);
  const legs = parseLegs(root.legs ?? attrs.legs);
  const protocolId = str(root.protocol_id) ?? str(root.protocolId) ?? highlights[0]?.protocolId;
  const missingRoles = strList2(group.missing_roles ?? attrs.missing_roles);
  const expectedRoles = strList2(group.expected_roles ?? attrs.expected_roles);
  const partial = Boolean(root.partial ?? attrs.partial ?? false);
  const awaiting = Boolean(
    group.awaiting ?? attrs.awaiting ?? (missingRoles.length > 0 || partial)
  );
  return {
    scene,
    highlights,
    legs,
    origin: str(root.origin) ?? str(attrs.origin),
    originChain: str(root.origin_chain) ?? str(attrs.origin_chain),
    messageId: str(root.message_id) ?? str(attrs.message_id),
    partial,
    protocolId,
    state: str(group.state) ?? str(attrs.state),
    awaiting,
    missingRoles,
    expectedRoles,
    expiresAt: num(group.expires_at) ?? num(attrs.expires_at)
  };
}

// src/storage/index.ts
var INLINE_BYTES_THRESHOLD = 1e4;
var LazyBody = class {
  constructor(fetch2) {
    this.fetch = fetch2;
  }
  fetch;
  cache = null;
  _loaded = false;
  inflight = null;
  async get() {
    if (this._loaded && this.cache !== null) return this.cache;
    if (!this.inflight) {
      this.inflight = this.fetch().then((v) => {
        this.cache = v;
        this._loaded = true;
        this.inflight = null;
        return v;
      });
    }
    return this.inflight;
  }
  get loaded() {
    return this._loaded;
  }
};
function loadEnvelope(header, fetchBody, inlineBody) {
  if (inlineBody) return { header, body: new LazyBody(async () => inlineBody) };
  if (!header.s3_key) {
    throw new Error(`Envelope ${header.canonical_tx_id} has neither an inline body nor an s3_key.`);
  }
  const key = header.s3_key;
  return { header, body: new LazyBody(() => fetchBody(key)) };
}
var SLICE_CORE = "core";
var SLICE_RAW = "raw";
var SLICE_EVENTS = "events";
var SLICE_DECODED = "decoded";
var OFFLOADABLE_SLICES = [SLICE_RAW, SLICE_EVENTS, SLICE_DECODED];
var SLICE_INFIXES = {
  [SLICE_RAW]: ".raw",
  [SLICE_EVENTS]: ".events",
  [SLICE_DECODED]: ".decoded"
};
var SLICE_FIELD_PATHS = {
  [SLICE_RAW]: ["raw_provider_payload"],
  [SLICE_EVENTS]: ["canonical_transaction", "events"],
  [SLICE_DECODED]: ["extras", "enrichment_layers"]
};
function sliceKey(coreS3Key, slice) {
  if (slice === SLICE_CORE) return coreS3Key;
  const infix = SLICE_INFIXES[slice];
  if (infix === void 0) return null;
  const marker = ".json";
  const idx = coreS3Key.lastIndexOf(marker);
  if (idx === -1) return null;
  return `${coreS3Key.slice(0, idx)}${infix}${coreS3Key.slice(idx)}`;
}
function bodyHasSlice(header, slice) {
  return !!header.body_slices && Object.prototype.hasOwnProperty.call(header.body_slices, slice);
}
function mergeSlice(body, slice, payload) {
  const path = SLICE_FIELD_PATHS[slice];
  if (!path || payload == null) return body;
  let cursor = body;
  for (const key of path.slice(0, -1)) {
    const next = cursor[key];
    if (typeof next !== "object" || next === null || Array.isArray(next)) {
      cursor[key] = {};
    }
    cursor = cursor[key];
  }
  cursor[path[path.length - 1]] = payload;
  return body;
}
var PLACEHOLDER_KEY = "_s3_offload";
function isOffloadPlaceholder(value) {
  return typeof value === "object" && value !== null && value[PLACEHOLDER_KEY] === true;
}
function makePlaceholder(opts) {
  return {
    [PLACEHOLDER_KEY]: true,
    bucket: opts.bucket,
    key: opts.key,
    size_bytes: opts.sizeBytes,
    attribute: opts.attribute
  };
}
async function rehydrateItem(item, fetcher) {
  const out = { ...item };
  await Promise.all(
    Object.entries(item).map(async ([attr, value]) => {
      if (!isOffloadPlaceholder(value)) return;
      try {
        out[attr] = await fetcher(value);
      } catch {
        out[attr] = value;
      }
    })
  );
  return out;
}
function safeLocalStorage() {
  try {
    const s2 = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s2 && typeof s2.getItem === "function" ? s2 : void 0;
  } catch {
    return void 0;
  }
}
var STORAGE_NAMESPACE = "authe";
function storageKey(...parts) {
  return [STORAGE_NAMESPACE, ...parts.map(String).filter(Boolean)].join(":");
}
function readLocalJson(key, fallback) {
  const value = readLocalJsonOptional(key);
  return value === void 0 ? fallback : value;
}
function readLocalJsonOptional(key) {
  const store = safeLocalStorage();
  if (!store) return void 0;
  try {
    const raw = store.getItem(key);
    if (raw === null) return void 0;
    return JSON.parse(raw);
  } catch {
    return void 0;
  }
}
function writeLocalJson(key, value) {
  const store = safeLocalStorage();
  if (!store) return false;
  try {
    store.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}
function removeLocalKey(key) {
  safeLocalStorage()?.removeItem(key);
}
function localKeysWithPrefix(prefix) {
  const store = safeLocalStorage();
  if (!store) return [];
  const out = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key != null && key.startsWith(prefix)) out.push(key);
  }
  return out;
}
function clearLocalPrefix(prefix) {
  const store = safeLocalStorage();
  if (!store) return 0;
  const keys = localKeysWithPrefix(prefix);
  for (const key of keys) store.removeItem(key);
  return keys.length;
}
var STORAGE_LEVELS = [
  "user",
  "tenant",
  "org",
  "customer",
  "app",
  "global"
];
function isGlobalish(value) {
  if (value == null) return true;
  const trimmed = value.trim();
  return trimmed === "" || trimmed === "main";
}
function identitySegments(identity, level) {
  const segs = [STORAGE_NAMESPACE];
  if (level === "global") return segs;
  if (identity.app) segs.push(String(identity.app));
  if (level === "app") return segs;
  if (!isGlobalish(identity.customerId)) segs.push("c", String(identity.customerId));
  if (level === "customer") return segs;
  if (!isGlobalish(identity.orgId)) segs.push("o", String(identity.orgId));
  if (level === "org") return segs;
  if (!isGlobalish(identity.tenantId)) segs.push("t", String(identity.tenantId));
  if (level === "tenant") return segs;
  if (identity.userId) segs.push("u", String(identity.userId));
  return segs;
}
var ScopedStorage = class _ScopedStorage {
  identity;
  /** The level `set`/`remove` use when none is given. */
  defaultLevel;
  constructor(identity) {
    this.identity = { ...identity };
    this.defaultLevel = identity.userId ? "user" : !isGlobalish(identity.tenantId) ? "tenant" : !isGlobalish(identity.orgId) ? "org" : "app";
  }
  /** The fully-namespaced key for `name` at `level`. */
  keyAt(name, level) {
    return [...identitySegments(this.identity, level), name].join(":");
  }
  /** Read exactly at one level (no fallback up the hierarchy). */
  getAt(name, level, fallback) {
    const value = readLocalJsonOptional(this.keyAt(name, level));
    return value === void 0 ? fallback : value;
  }
  /** Hierarchical read: most specific stored value wins, else `fallback`. */
  get(name, fallback) {
    const tried = /* @__PURE__ */ new Set();
    for (const level of STORAGE_LEVELS) {
      const key = this.keyAt(name, level);
      if (tried.has(key)) continue;
      tried.add(key);
      const value = readLocalJsonOptional(key);
      if (value !== void 0) return value;
    }
    return fallback;
  }
  /** Write at `level` (default: `defaultLevel`). */
  set(name, value, level = this.defaultLevel) {
    return writeLocalJson(this.keyAt(name, level), value);
  }
  /** Remove at `level` (default: `defaultLevel`). */
  remove(name, level = this.defaultLevel) {
    removeLocalKey(this.keyAt(name, level));
  }
  /** Remove every key at `level` and narrower for this identity. Returns the count. */
  clear(level) {
    return clearLocalPrefix(`${identitySegments(this.identity, level).join(":")}:`);
  }
  /** Derive a new scope with some identity fields changed (e.g. switch tenant). */
  with(patch) {
    return new _ScopedStorage({ ...this.identity, ...patch });
  }
};
function createScopedStorage(identity) {
  return new ScopedStorage(identity);
}
function rewriteToCdn(url, config2) {
  try {
    const u = new URL(url);
    if (config2.s3Host && u.host !== config2.s3Host) return url;
    u.host = config2.cdnHost;
    u.protocol = "https:";
    if (config2.cdnPathPrefix && !u.pathname.startsWith(config2.cdnPathPrefix)) {
      u.pathname = `${config2.cdnPathPrefix.replace(/\/$/, "")}${u.pathname}`;
    }
    return u.toString();
  } catch {
    return url;
  }
}
function cdnUrlForKey(s3Key, config2) {
  const prefix = config2.cdnPathPrefix ? `/${config2.cdnPathPrefix.replace(/^\/|\/$/g, "")}` : "";
  const key = s3Key.replace(/^\//, "");
  return `https://${config2.cdnHost}${prefix}/${key}`;
}

export { BulkLoader, CdnAccessDeniedError, DECODED_ENVELOPE_MARKER, INLINE_BYTES_THRESHOLD, LazyBody, OFFLOADABLE_SLICES, PLACEHOLDER_KEY, SLICE_CORE, SLICE_DECODED, SLICE_EVENTS, SLICE_FIELD_PATHS, SLICE_INFIXES, SLICE_RAW, STORAGE_LEVELS, STORAGE_NAMESPACE, ScopedStorage, ScopedTxCache, bodyHasSlice, buildAddressIndex, capDistinct, cdnBaseUrl, cdnKeyDenialReason, cdnUrlForKey, clearCdnSession, clearLocalPrefix, coerceTxStatus, configureCdnDelivery, createEnvelopeBodyLoader, createIdbKvStore, createMemoryKvStore, createScopedStorage, createScopedTxCache, decodedOf, displayOf, ensureCdnSession, fetchSignedCdnJson, getCdnSession, indexedDbAvailable, invalidateCdnSession, isCdnKeyReadable, isCreationCallType, isEnriched, isOffloadPlaceholder, isPrivateAssetCdnKey, isPrivateCdnKey, isPublicCdnKey, kindOf, loadEnvelope, localKeysWithPrefix, looksLikeAddress, makePlaceholder, mergeSlice, parseCrossChainRefs, parseDecodeMeta, parseDecodedCall, parseDecodedCallValue, parseEntityHints, parseEvents, parseGroupView, parseInternalTxs, parseKeyAttributeGroups, parseKeyAttributes, parseRenderHints, parseTransfers, parseTxBody, parseTxStatus, prepareSignedCdnImage, rawOf, readLocalJson, readLocalJsonOptional, rehydrateItem, removeLocalKey, rewriteToCdn, signedCdnUrl, sliceKey, storageKey, versionedKey, writeLocalJson };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map